import React from "react";
import { useEffect, useState } from "react";
import usePost from "../../hooks/usePost";
import format from "date-fns/format";
import PortalOpsStageBasedCtas from "./PortalOpsStageBasedCtas";
import PortalTailNumberInput from "./PortalTailNumberInput";
import { wifiList, cateringList, yesNoList } from "../../data/portalFormListData";
import SelectInput from "../ui/SelectInput";
import { currencyList } from "../../data/currencyList";
import TextInput from "../ui/TextInput";
import NumberInput from "../ui/NumberInput";
import PortalVipTerminalInput from "./PortalVipTerminalInput";
import LoadingOverlay from "../shared/LoadingOverlay";
import TextAreaInput from "../ui/TextAreaInput";
import chevronIcon from "../../assets/icons/chevron-down-white.svg";
import { getTripTypeLabelFromLegCount } from "../../utilities/tripTypeDisplay";
import PortalDealDetailsEditToolbar from "./PortalDealDetailsEditToolbar";

const PortalOpsDetails = ({ deal, legs }) => {
    const { sending, postData } = usePost();

    const [isEditMode, setIsEditMode] = useState(false);

    const [didSetDeal, setDidSetDeal] = useState(false);

    const paramLegs = legs || [];
    const [aircraftTail, setAircraftTail] = useState("");
    const [aircraftOp, setAircraftOp] = useState("");
    const [aircraftType, setAircraftType] = useState("");
    const [aircraftCat, setAircraftCat] = useState("");

    const [selectedPrefCurrency, setSelectedPrefCurrency] = useState("");
    const [selMovida, setSelMovida] = useState("");
    const [selectedKycHeat, setSelectedKycHeat] = useState("");
    const [currentNotes, setCurrentNotes] = useState("");
    const [numPets, setNumPets] = useState(0);

    const [selectedWifi, setSelectedWifi] = useState("");
    const [selectedCatering, setSelectedCatering] = useState("");
    const [currentOpsNotes, setCurrentOpsNotes] = useState("");

    const [contractName, setContractName] = useState("");

    const [legTerminals, setLegTerminals] = useState([]);

    const [numPilots, setNumPilots] = useState(0);
    const [numFuelStops, setNumFuelStops] = useState(0);

    const [hasFlightAttendant, setHasFlightAttendant] = useState("");
    const [FFOverride, setFFOverride] = useState(false);

    // Expandable leg sections
    const [expandedLegs, setExpandedLegs] = useState({});

    useEffect(() => {
        if (deal && !didSetDeal) {
            setAircraftTail(deal.tail_No);
            setAircraftOp(
                deal.flight_Operator_Test && deal.flight_Operator_Test.length > 0 ? deal.flight_Operator_Test[0] : ""
            );
            setAircraftType(deal.aircraft_Type_NEW);
            setAircraftCat(deal.aircraft_Category_NEW);

            setSelectedPrefCurrency(deal.preferred_Currency);
            setSelectedKycHeat(deal.kyC_Heat);
            setSelMovida(deal.show_MOVIDA_in_Quote);
            setCurrentNotes(deal.notes_on_Flight);
            setNumPets(deal.number_of_Pets ? parseInt(deal.number_of_Pets) : 0);

            setSelectedWifi(deal.wiFi);
            setSelectedCatering(deal.catering_option);
            setCurrentOpsNotes(deal.catering);

            setContractName(deal.contract_Display_Name);

            setNumPilots(deal.pilots ?? 0);
            setNumFuelStops(deal.fuel_Stops ?? 0);

            setHasFlightAttendant(deal.has_Flight_Attendant);

            setFFOverride(deal.fF_override);

            setDidSetDeal(true);
        }
    }, [deal, didSetDeal]);

    // Expand first leg only by default (match sales)
    useEffect(() => {
        if (legs && legs.length > 0) {
            const sorted = [...legs].sort((a, b) => (a.leg_Number < b.leg_Number ? -1 : 1));
            setExpandedLegs({ [sorted[0].id]: true });

            const termsData = legs.map((leg) => {
                return {
                    legId: leg.id,
                    terminalFrom: leg.leg_Airport_From_terminal || "",
                    terminalTo: leg.leg_Airport_To_terminal || "",
                };
            });
            setLegTerminals(termsData);
        }
    }, [legs]);

    const updateTail = (newTail) => {
        setAircraftTail(newTail.id);
        setAircraftOp(newTail.operatorName);
        setAircraftType(newTail.aircraftName);
        setAircraftCat(newTail.category);
    };

    const toggleLeg = (legId) => {
        setExpandedLegs((prev) => ({
            ...prev,
            [legId]: !prev[legId],
        }));
    };

    const updateLegTerminalData = (terminal, legId, terminalType) => {
        const updatedTermsData = legTerminals.map((term) => {
            if (term.legId === legId) {
                return {
                    ...term,
                    terminalFrom: terminalType === "from" ? terminal : term.terminalFrom,
                    terminalTo: terminalType === "to" ? terminal : term.terminalTo,
                };
            } else {
                return term;
            }
        });

        if (updatedTermsData.filter((term) => term.legId === legId).length === 0) {
            updatedTermsData.push({
                legId: legId,
                terminalFrom: terminalType === "from" ? terminal : "",
                terminalTo: terminalType === "to" ? terminal : "",
            });
        }

        setLegTerminals(updatedTermsData);
        console.log(updatedTermsData);
    };

    const updateDeal = () => {
        let formData = {};

        formData = {
            dealId: deal.id,
            KYC_Heat: selectedKycHeat,
            Preferred_Currency: selectedPrefCurrency,
            Show_MOVIDA_in_Quote: selMovida,
            Number_of_Pets: numPets,
            Notes_on_Flight: currentNotes ?? "",
            WiFi: selectedWifi,
            catering_option: selectedCatering,
            Catering: currentOpsNotes ?? "",

            Flight_Operator_Test: [aircraftOp],
            Aircraft_Type_NEW: aircraftType,
            Aircraft_Category_NEW: aircraftCat,
            Tail_No: aircraftTail,

            Contract_Display_Name: contractName,

            Pilots: numPilots,
            Fuel_Stops: numFuelStops,

            Has_Flight_Attendant: hasFlightAttendant,
            FF_override: FFOverride,

            legTerminalData: legTerminals,
        };

        postData(formData, "/api/portal/update-deal");

        //console.log(formData)
    };

    const resetDealFieldsFromProps = () => {
        setAircraftTail(deal.tail_No);
        setAircraftOp(
            deal.flight_Operator_Test && deal.flight_Operator_Test.length > 0 ? deal.flight_Operator_Test[0] : ""
        );
        setAircraftType(deal.aircraft_Type_NEW);
        setAircraftCat(deal.aircraft_Category_NEW);
        setSelectedPrefCurrency(deal.preferred_Currency);
        setSelectedKycHeat(deal.kyC_Heat);
        setSelMovida(deal.show_MOVIDA_in_Quote);
        setCurrentNotes(deal.notes_on_Flight);
        setNumPets(deal.number_of_Pets ? parseInt(deal.number_of_Pets) : 0);
        setSelectedWifi(deal.wiFi);
        setSelectedCatering(deal.catering_option);
        setCurrentOpsNotes(deal.catering);
        setContractName(deal.contract_Display_Name);
        setNumPilots(deal.pilots ?? 0);
        setNumFuelStops(deal.fuel_Stops ?? 0);
        setHasFlightAttendant(deal.has_Flight_Attendant);
        setFFOverride(deal.fF_override);

        if (legs?.length) {
            setLegTerminals(
                legs.map((leg) => ({
                    legId: leg.id,
                    terminalFrom: leg.leg_Airport_From_terminal || "",
                    terminalTo: leg.leg_Airport_To_terminal || "",
                }))
            );
        }
    };

    const handleCancelEdit = () => {
        resetDealFieldsFromProps();
        setIsEditMode(false);
    };

    const handleSaveEdit = () => {
        updateDeal();
        setIsEditMode(false);
    };

    return (
        deal && (
            <>
                <div className="portal_ops_layout">
                    <div className="deal_details_form">
                        <div>
                            <div className="formRow">
                                <label>Requested Date:</label> {format(new Date(deal.date_Opened), "dd/MM/yy")}
                            </div>
                            <div className="formRow">
                                <label>Service:</label> {deal.service}
                            </div>
                            <div className="formRow">
                                <label>Trip Type:</label> {getTripTypeLabelFromLegCount(paramLegs.length) || "-"}
                            </div>
                            <div className="formRow">
                                <label>Preferred Currency:</label>
                                {isEditMode ? (
                                    <SelectInput
                                        options={currencyList}
                                        value={(value) => setSelectedPrefCurrency(value)}
                                        defaultValue={deal.preferred_Currency}
                                        label=""
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    deal.preferred_Currency || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Contract Name:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        defaultValue={contractName}
                                        label=""
                                        value={(fromValue) => setContractName(fromValue)}
                                    />
                                ) : (
                                    contractName || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>SHYF Number:</label> {deal.shyF_No}
                            </div>
                            <div className="formRow">
                                <label>Num Pets:</label>{" "}
                                {isEditMode ? (
                                    <fieldset className="fieldset fieldset--margin-bottom">
                                        <div className="text-input text-input--margin-bottom">
                                            <input
                                                type="text"
                                                autoComplete="on"
                                                className="text-input__input"
                                                defaultValue={deal.number_of_Pets ?? 0}
                                                onChange={(e) => setNumPets(parseInt(e.target.value))}
                                            />
                                            <label className="label label--minimised"></label>
                                        </div>
                                    </fieldset>
                                ) : (
                                    (deal.number_of_Pets ?? 0)
                                )}
                            </div>
                            <div className="formRow">
                                <label>Catering:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        options={cateringList}
                                        value={(value) => setSelectedCatering(value)}
                                        defaultValue={deal.catering_option}
                                        label=""
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    deal.catering_option || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>WiFi:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        options={wifiList}
                                        value={(value) => setSelectedWifi(value)}
                                        defaultValue={deal.wiFi}
                                        label=""
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    deal.wiFi || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Fuel Stops:</label>{" "}
                                {isEditMode ? (
                                    <NumberInput
                                        label=""
                                        defaultValue={numFuelStops}
                                        value={(newValue) => setNumFuelStops(newValue)}
                                    />
                                ) : (
                                    numFuelStops
                                )}
                            </div>
                            <div className="formRow">
                                <label>Pilots:</label>{" "}
                                {isEditMode ? (
                                    <NumberInput
                                        label=""
                                        defaultValue={numPilots}
                                        value={(newValue) => setNumPilots(newValue)}
                                    />
                                ) : (
                                    numPilots
                                )}
                            </div>
                            <div className="formRow">
                                <label>Flight Attendant:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        value={(value) => setHasFlightAttendant(value)}
                                        options={yesNoList}
                                        label=""
                                        required
                                        defaultValue={deal.has_Flight_Attendant}
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    deal.has_Flight_Attendant || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Ops Notes:</label>{" "}
                                {isEditMode ? (
                                    <TextAreaInput
                                        maxLength={2000}
                                        showCharacterCount
                                        defaultValue={deal.catering}
                                        value={(fromValue) => setCurrentOpsNotes(fromValue)}
                                        label=""
                                    />
                                ) : (
                                    deal.catering || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Operator:</label> {aircraftOp || "-"}
                            </div>
                            <div className="formRow">
                                <label>Aircraft Category:</label> {aircraftCat || "-"}
                            </div>
                            <div className="formRow">
                                <label>Aircraft Type:</label> {aircraftType || "-"}
                            </div>
                            <div className="formRow">
                                <label>Tail:</label> {aircraftTail || "-"}
                            </div>
                            <div className="formRow">
                                <label>FF Override:</label>{" "}
                                {isEditMode ? (
                                    <input
                                        type="checkbox"
                                        onChange={(e) => setFFOverride(e.target.checked)}
                                        defaultChecked={deal.fF_override}
                                        name="fF_override"
                                    />
                                ) : deal.fF_override ? (
                                    "Yes"
                                ) : (
                                    "No"
                                )}
                            </div>
                            {isEditMode && (
                                <div className="formRow">
                                    <label>Change Tail:</label>
                                    <PortalTailNumberInput
                                        defaultValue={aircraftTail}
                                        value={(fromValue) => updateTail(fromValue)}
                                    />
                                </div>
                            )}
                        </div>
                        <div>{/* right column intentionally left empty in ops main form */}</div>
                    </div>

                    {paramLegs && paramLegs.length > 0 && (
                        <div className="deal_details_form deal_details_form--ops-main">
                            <div className="portal-details__legs">
                                <PortalDealDetailsEditToolbar
                                    isEditMode={isEditMode}
                                    onEdit={() => setIsEditMode(true)}
                                    onSave={handleSaveEdit}
                                    onCancel={handleCancelEdit}
                                    sending={sending}
                                />
                                {paramLegs
                                    .sort((a, b) => {
                                        return a.leg_Number < b.leg_Number ? -1 : 1;
                                    })
                                    .map((leg, index) => {
                                        const isExpanded = expandedLegs[leg.id];
                                        const legDate = new Date(leg.leg_Date_and_Time);
                                        return (
                                            <div key={leg.id} className="leg-section">
                                                <div
                                                    className="leg-header-clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-expanded={isExpanded}
                                                    onClick={() => toggleLeg(leg.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            toggleLeg(leg.id);
                                                        }
                                                    }}
                                                >
                                                    <strong>
                                                        Leg {index + 1} - {leg.airport_From} - {leg.airport_To}
                                                    </strong>
                                                    <img
                                                        src={chevronIcon}
                                                        className={
                                                            isExpanded ? "leg-header-clickable__chevron--open" : ""
                                                        }
                                                        alt=""
                                                    />
                                                </div>
                                                {isExpanded && (
                                                    <div className="leg-details">
                                                        <div className="formRow">
                                                            <label>From:</label> {leg.airport_From}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>To:</label> {leg.airport_To}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Departure Date:</label> {format(legDate, "dd/MM/yy")}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Departure Time:</label> {format(legDate, "HH:mm")}
                                                        </div>
                                                        {deal.leg_1_Date_of_Arrival && index === 0 && (
                                                            <>
                                                                <div className="formRow">
                                                                    <label>Arrival Date:</label>{" "}
                                                                    {format(
                                                                        new Date(deal.leg_1_Date_of_Arrival),
                                                                        "dd/MM/yy"
                                                                    )}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Arrival Time:</label>{" "}
                                                                    {deal.leg_1_Time_of_Arrival || "-"}
                                                                </div>
                                                            </>
                                                        )}
                                                        {deal.leg_2_Date_of_Arrival && index === 1 && (
                                                            <>
                                                                <div className="formRow">
                                                                    <label>Arrival Date:</label>{" "}
                                                                    {format(
                                                                        new Date(deal.leg_2_Date_of_Arrival),
                                                                        "dd/MM/yy"
                                                                    )}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Arrival Time:</label>{" "}
                                                                    {deal.leg_2_Time_of_Arrival || "-"}
                                                                </div>
                                                            </>
                                                        )}
                                                        <div className="formRow">
                                                            <label>Pax:</label> {leg.number_of_Passengers}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Bags:</label> {leg.number_of_Bags}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Carry On:</label> {leg.number_of_Carry_On_Bags}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Leg Terminal From:</label>{" "}
                                                            {isEditMode ? (
                                                                <PortalVipTerminalInput
                                                                    value={(fromValue) =>
                                                                        updateLegTerminalData(
                                                                            fromValue.name,
                                                                            leg.id,
                                                                            "from"
                                                                        )
                                                                    }
                                                                    defaultValue={leg.leg_Airport_From_terminal}
                                                                />
                                                            ) : (
                                                                leg.leg_Airport_From_terminal || "-"
                                                            )}
                                                        </div>
                                                        <div className="formRow">
                                                            <label>Leg Terminal To:</label>{" "}
                                                            {isEditMode ? (
                                                                <PortalVipTerminalInput
                                                                    value={(fromValue) =>
                                                                        updateLegTerminalData(
                                                                            fromValue.name,
                                                                            leg.id,
                                                                            "to"
                                                                        )
                                                                    }
                                                                    defaultValue={leg.leg_Airport_To_terminal}
                                                                />
                                                            ) : (
                                                                leg.leg_Airport_To_terminal || "-"
                                                            )}
                                                        </div>
                                                        {/* {deal && !isDealPreAvinode(deal) && index === 0 && (
                                                                <>
                                                                    <div className="formRow">
                                                                        <label>Leg 1 Terminal From:</label>
                                                                        <PortalVipTerminalInput
                                                                            value={(fromValue) =>
                                                                                setLeg1TerminalFrom(fromValue.name)
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <div className="formRow">
                                                                        <label>Leg 1 Terminal To:</label>
                                                                        <PortalVipTerminalInput
                                                                            value={(fromValue) =>
                                                                                setLeg1TerminalTo(fromValue.name)
                                                                            }
                                                                        />
                                                                    </div>
                                                                </>
                                                            )} */}
                                                        {/* {deal &&
                                                                !isDealPreAvinode(deal) &&
                                                                index === 1 &&
                                                                paramLegs.length > 1 && (
                                                                    <>
                                                                        <div className="formRow">
                                                                            <label>Leg 2 Terminal From:</label>
                                                                            <PortalVipTerminalInput
                                                                                value={(fromValue) =>
                                                                                    setLeg2TerminalFrom(fromValue.name)
                                                                                }
                                                                            />
                                                                        </div>
                                                                        <div className="formRow">
                                                                            <label>Leg 2 Terminal To:</label>
                                                                            <PortalVipTerminalInput
                                                                                value={(fromValue) =>
                                                                                    setLeg2TerminalTo(fromValue.name)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )} */}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>

                {deal && <PortalOpsStageBasedCtas deal={deal} />}
                {sending && <LoadingOverlay />}
            </>
        )
    );
};

export default PortalOpsDetails;
